import { timestampDate } from '@bufbuild/protobuf/wkt';
import type { Workspace } from '@sunstone/api-client/gen/deployments/v1/deployments_pb';
import { WorkspaceUpgradeStatus } from '@sunstone/api-client/gen/deployments/v1/deployments_pb';
import { cn, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@sunstone/ui';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { ReleaseLifecycleBadge } from './ReleaseLifecycleBadge';

/** Shown in place of a bare "—" when a workspace has no currentVersion at all — e.g. a Duplo-discovered tenant with no apps.tfvars.json backfilled yet. */
export const VERSION_UNKNOWN_LABEL = 'Version not found';

/** Upgrade states shown as a `{cur} → {tar}` transition with a status tooltip, rather than the plain current-version + lifecycle badge. */
export const IN_FLIGHT_STATES = new Set<WorkspaceUpgradeStatus>([
  WorkspaceUpgradeStatus.SCHEDULED,
  WorkspaceUpgradeStatus.IN_PROGRESS,
  WorkspaceUpgradeStatus.ROLLING_BACK,
  WorkspaceUpgradeStatus.FAILED,
]);

/** Upgrade states that block starting a new sync/promote. Unlike IN_FLIGHT_STATES, excludes FAILED so a failed upgrade can be retried. */
export const ACTION_BLOCKING_STATES = new Set<WorkspaceUpgradeStatus>([
  WorkspaceUpgradeStatus.SCHEDULED,
  WorkspaceUpgradeStatus.IN_PROGRESS,
  WorkspaceUpgradeStatus.ROLLING_BACK,
]);

const STATUS_TARGET_COLOR: Partial<Record<WorkspaceUpgradeStatus, string>> = {
  [WorkspaceUpgradeStatus.SCHEDULED]: 'text-blue-500',
  [WorkspaceUpgradeStatus.IN_PROGRESS]: 'text-blue-500',
  [WorkspaceUpgradeStatus.ROLLING_BACK]: 'text-amber-500',
  [WorkspaceUpgradeStatus.FAILED]: 'text-destructive',
};

const STATUS_LABEL: Partial<Record<WorkspaceUpgradeStatus, string>> = {
  [WorkspaceUpgradeStatus.SCHEDULED]: 'Upgrade scheduled',
  [WorkspaceUpgradeStatus.IN_PROGRESS]: 'Upgrade in progress',
  [WorkspaceUpgradeStatus.ROLLING_BACK]: 'Rolling back',
  [WorkspaceUpgradeStatus.FAILED]: 'Upgrade failed',
};

interface WorkspaceVersionStatusProps {
  workspace: Workspace;
  className?: string;
}

/**
 * Renders a workspace's current version with one of two trailing affordances:
 *   - When an upgrade is in flight (SCHEDULED / IN_PROGRESS / ROLLING_BACK /
 *     FAILED) and a target_version is set: shows `{cur} → {tar}` with the
 *     target coloured by status and a tooltip describing the state.
 *   - Otherwise: shows `{cur}` followed by a lifecycle badge
 *     (Latest / Upgrade available / Deprecated).
 */
export function WorkspaceVersionStatus({ workspace, className }: WorkspaceVersionStatusProps) {
  const inFlight = IN_FLIGHT_STATES.has(workspace.upgradeStatus);
  const hasTransition =
    inFlight && workspace.targetVersion && workspace.targetVersion !== workspace.currentVersion;

  if (hasTransition) {
    const color = STATUS_TARGET_COLOR[workspace.upgradeStatus] ?? 'text-blue-500';
    const pulse = workspace.upgradeStatus === WorkspaceUpgradeStatus.IN_PROGRESS;
    const isRollback = workspace.upgradeStatus === WorkspaceUpgradeStatus.ROLLING_BACK;
    const Arrow = isRollback ? ArrowLeft : ArrowRight;
    const scheduledDate = workspace.upgradeScheduledAt
      ? timestampDate(workspace.upgradeScheduledAt).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
        })
      : null;
    const label = STATUS_LABEL[workspace.upgradeStatus] ?? '';

    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className={cn('inline-flex items-center gap-1.5 cursor-help', className)}>
              <span>{workspace.currentVersion || '—'}</span>
              <Arrow className={cn('w-3 h-3', color, pulse && 'animate-pulse')} />
              <span className={cn(color, pulse && 'animate-pulse')}>{workspace.targetVersion}</span>
            </span>
          </TooltipTrigger>
          <TooltipContent>
            {label}
            {scheduledDate ? ` · ${scheduledDate}` : ''}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <span className={cn('inline-flex items-center gap-1.5', className)}>
      <span>{workspace.currentVersion || VERSION_UNKNOWN_LABEL}</span>
      <ReleaseLifecycleBadge workspace={workspace} />
    </span>
  );
}
